import React from "react";
import { createBrowserRouter } from "react-router-dom";

import { getEnv } from "@/env";
import * as PATHS from "@/react/routing/constants";

import { VccPage } from "@/react/features/vcc/VccPage";
import { AgentGroupsPage } from "@/react/features/vcc/agent-groups/AgentGroupsPage";
import { AgentsPage } from "@/react/features/vcc/agents";
import { LicensesPage } from "@/react/features/smp/licenses/LicensesPage";
import { SmsCampaignPage } from "@/react/features/support-tools/sms-campaign/SmsCampaignPage";
import { SmsChannelsPage } from "@/react/features/vcc/sms-channels/SmsChannelsPage";
import {
  GlobalizationInternationalComparePage,
  GlobalizationInternationalPage,
  GlobalizationSubnetPage,
} from "@/react/features/globalization";
import { GlobalizationInternationalEditPage } from "@/react/features/globalization/pages/GlobalizationInternationalEditPage";
import TokenGeneratorPage from "@/react/features/token-generator/TokenGeneratorPage";
import { PhoneChannelsPage } from "@/react/features/vcc/phone-channels/PhoneChannelsPage";
import { EmailChannelsPage } from "@/react/features/vcc/email-channels/EmailChannelsPage";
import TenantInfoPage from "@/react/features/vcc/tenant/TenantInfoPage";
import { Layout } from "@/react/components/Layout";
import GlobeIcon from "@8x8/oxygen-icon/icons/GlobeIcon";
import TemplateIcon from "@8x8/oxygen-icon/icons/TemplateIcon";
import AttendantIcon from "@8x8/oxygen-icon/icons/AttendantIcon";
import InboxTrayIcon from "@8x8/oxygen-icon/novo/InboxTrayIcon";
import {
  AUDIT_READ,
  BULK_SERVICES_READ,
  EMAIL_SUPPORT_READ,
  FRAUD_SUPPORT_READ,
  LICENSES_READ,
  PORT_ORDERS_READ,
  SEND_EMAILS_READ,
  SUPPORT_TOOLS_READ,
  VCC_READ,
} from "@/react/services/rbac/permissions";
import { HeadsetIcon, IdentityManagementIcon } from "@8x8/oxygen-icon";
import LiveMonitorIcon from "@8x8/oxygen-icon/icons/LiveMonitorIcon";
import { RequiredAuth } from "@/react/features/auth/RequiredAuth";
import { PbxPage } from "@/react/features/smp/pbx/PbxPage";
import { ErrorBoundary } from "@/react/components/ErrorBoundary";
import { LoginCallback } from "@okta/okta-react";
import { OverlaySpinner } from "@/react/components/OverlaySpinner";
import RingGroupsPage from "@/react/features/smp/ring-groups/RingGroupsPage";
import { EmailStatusPage } from "@/react/features/support-tools/email-status";
import CallQueuesPage from "@/react/features/smp/call-queues/CallQueuesPage";
import ExtensionsPage from "@/react/features/support-tools/extensions/ExtensionsPage";
import AuditPage from "@/react/features/support-tools/audit-trail/AuditPage";
import DidCleanupPage from "@/react/features/support-tools/did-cleanup/DidCleanupPage";
import { AccessGroupingPage } from "@/react/features/support-tools/access-grouping";
import { EncryptSecretsPage } from "@/react/features/support-tools/encryption";
import { CustomerServiceRecordPage } from "@/react/features/support-tools/csr";
import TranscribePage from "@/react/features/transcribe/TranscribePage";
import UploadIcon from "@8x8/oxygen-icon/icons/UploadIcon";
import { SendEmailPage } from "@/react/features/send-email";
import PrimaryAdminChangePage from "@/react/features/support-tools/primary-admin-change/PrimaryAdminChangePage";
import { QueuesPage } from "@/react/features/vcc/queues";
import TrunkGatewaysPage from "@/react/features/support-tools/siptrunk/pages/TrunkGatewaysPage";
import TrunkGatewayEditPage from "@/react/features/support-tools/siptrunk/pages/TrunkGatewayEditPage";
import TrunkGatewayCreatePage from "@/react/features/support-tools/siptrunk/pages/TrunkGatewayCreatePage";
import { UnbindVmmsPage } from "@/react/features/support-tools/unbind-vmms";
import InternationalCallForwardingPage from "@/react/features/support-tools/international-call-forwarding/InternationalCallForwardingPage";
import { BLFCountPage } from "@/react/features/support-tools/blf-count";
import { AgentAssignmentsPage } from "@/react/features/vcc/agent-assignments";
import { EmailSuppressionPage } from "@/react/features/support-tools/email-suppression";
import DialplansPage from "@/react/features/support-tools/dialplans/DialplansPage";
import JobInventoryPage from "@/react/features/job-inventory/JobInventoryPage";
import NomadicPage from "@/react/features/support-tools/nomadic-911/NomadicPage";

const ENABLE_ACCESS_GROUPING_TOOL = getEnv(
  "VUE_APP_ENABLE_ACCESS_GROUPING_TOOL",
  true
);

const ENABLE_JOB_INVENTORY = getEnv(
  "VUE_APP_ENABLE_JOB_INVENTORY",
  true,
  false
);

export const routes = [
  {
    path: "/",
    element: <RequiredAuth />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: PATHS.GLOBALIZATION_BASE,
    label: "Globalization",
    icon: <GlobeIcon />,
    element: <RequiredAuth />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: PATHS.GLOBALIZATION_INTERNATIONAL,
        label: "Global Information",
        element: <RequiredAuth />,
        children: [
          {
            index: true,
            path: PATHS.GLOBALIZATION_INTERNATIONAL,
            element: <GlobalizationInternationalPage />,
          },
          {
            path: PATHS.GLOBALIZATION_INTERNATIONAL_COMPARE,
            element: <GlobalizationInternationalComparePage />,
          },
          {
            path: PATHS.GLOBALIZATION_INTERNATIONAL_EDIT,
            element: <GlobalizationInternationalEditPage />,
          },
        ],
      },
      {
        path: PATHS.GLOBALIZATION_SUBNET,
        label: "Subnet List",
        element: <GlobalizationSubnetPage />,
      },
    ],
  },
  {
    path: PATHS.BULK_SERVICES,
    label: "Bulk Services",
    icon: <TemplateIcon />,
    element: <RequiredAuth />,
    errorElement: <ErrorBoundary />,
    permission: BULK_SERVICES_READ,
    children: [
      {
        path: PATHS.BULK_SERVICES + "/pbx",
        label: "PBXs",
        element: <PbxPage />,
      },
      {
        path: PATHS.BULK_SERVICES + "/port-orders",
        label: "Port Orders",
        permission: PORT_ORDERS_READ,
      },
      {
        path: PATHS.BULK_SERVICES + "/ring-groups",
        label: "Ring Groups",
        element: <RingGroupsPage />,
      },
      {
        path: PATHS.BULK_SERVICES + "/call-queues",
        label: "Call Queues",
        element: <CallQueuesPage />,
      },
      {
        path: PATHS.LICENSES,
        label: "Licenses",
        element: <LicensesPage />,
        permission: LICENSES_READ,
      },
      {
        path: PATHS.BULK_SERVICES + "/send-emails",
        label: "Send Emails",
        element: <SendEmailPage />,
        permission: SEND_EMAILS_READ,
      },
    ],
  },
  {
    path: PATHS.SUPPORT_TOOLS,
    label: "Support Tools",
    icon: <AttendantIcon />,
    element: <RequiredAuth />,
    errorElement: <ErrorBoundary />,
    permission: SUPPORT_TOOLS_READ,
    children: [
      {
        path: PATHS.SUPPORT_TOOLS_AUDITS,
        label: "Audit",
        permission: AUDIT_READ,
        element: <AuditPage />,
      },
      {
        path: PATHS.SUPPORT_TOOLS_SMS_CAMPAIGN,
        label: "SMS Campaign",
        element: <SmsCampaignPage />,
      },
      {
        path: PATHS.SUPPORT_TOOLS_EXTENSIONS_UPDATE,
        label: "Extensions Update",
        element: <ExtensionsPage />,
      },
      {
        path: PATHS.SUPPORT_TOOLS_BLFCHECK,
        label: "BLF Count Check",
        element: <BLFCountPage />,
      },
      {
        path: PATHS.SUPPORT_TOOLS_EMAIL_STATUS,
        label: "Email Status",
        element: <EmailStatusPage />,
        permission: EMAIL_SUPPORT_READ,
      },
      {
        path: PATHS.SUPPORT_TOOLS_EMAIL_SUPPRESSION,
        label: "Email Suppression",
        element: <EmailSuppressionPage />,
        permission: EMAIL_SUPPORT_READ,
      },
      {
        path: PATHS.SUPPORT_TOOLS_UNBIND_VMMS,
        label: "Unbind VMMS DID",
        element: <UnbindVmmsPage />,
      },
      {
        path: PATHS.SUPPORT_TOOLS_DID_CLEANUP,
        label: "DID Cleanup",
        element: <DidCleanupPage />,
      },
      {
        path: PATHS.SUPPORT_TOOLS_CSR,
        label: "Customer Service Record",
        element: <CustomerServiceRecordPage />,
      },
      {
        path: PATHS.SUPPORT_TOOLS_ICF,
        label: "International Call Forwarding",
        element: <InternationalCallForwardingPage />,
      },
      {
        path: PATHS.SUPPORT_TOOLS_NOMADIC_911,
        label: "Nomadic 911",
        element: <NomadicPage />,
      },
      {
        path: PATHS.SUPPORT_TOOLS_DIAL_PLANS,
        label: "Dial Plans",
        element: <DialplansPage />,
      },
      {
        path: PATHS.SUPPORT_TOOLS_SIP_TRUNK,
        label: "SIP Trunk Config",
        element: <TrunkGatewaysPage />,
      },
      {
        // Edit page for SIP Trunk
        path: `${PATHS.SUPPORT_TOOLS_SIP_TRUNK}/:trunkId`,
        element: <TrunkGatewayEditPage />,
      },
      {
        // Create page for SIP Trunk
        path: `${PATHS.SUPPORT_TOOLS_SIP_TRUNK}/create`,
        element: <TrunkGatewayCreatePage />,
      },
      {
        path: PATHS.SUPPORT_TOOLS_ENCRYPT,
        label: "Encrypt Secrets",
        element: <EncryptSecretsPage />,
      },
      ...(ENABLE_ACCESS_GROUPING_TOOL
        ? [
            {
              path: PATHS.SUPPORT_TOOLS_ACCESS_GROUPING,
              label: "Access Grouping",
              element: <AccessGroupingPage />,
            },
          ]
        : []),
      {
        path: PATHS.SUPPORT_TOOLS + "/primary-admin",
        label: "Primary Admin",
        element: <PrimaryAdminChangePage />,
      },
    ],
  },
  {
    path: PATHS.VCC_BASE,
    label: "Contact Center",
    element: <RequiredAuth />,
    errorElement: <ErrorBoundary />,
    icon: <HeadsetIcon />,
    permission: VCC_READ,
    children: [
      {
        path: PATHS.VCC_TENANT_INFO,
        label: "Tenant Info",
        element: (
          <VccPage>
            <TenantInfoPage />
          </VccPage>
        ),
      },
      {
        path: PATHS.VCC_AGENT_GROUPS,
        label: "Agent Groups",
        element: (
          <VccPage>
            <AgentGroupsPage />
          </VccPage>
        ),
      },
      {
        path: PATHS.VCC_AGENTS,
        label: "Agents",
        element: (
          <VccPage>
            <AgentsPage />
          </VccPage>
        ),
      },
      {
        path: PATHS.VCC_AGENT_ASSIGNMENTS,
        label: "Agent Assignments",
        element: (
          <VccPage>
            <AgentAssignmentsPage />
          </VccPage>
        ),
      },
      {
        path: PATHS.VCC_SMS_CHANNELS,
        label: "SMS Channels",
        element: (
          <VccPage>
            <SmsChannelsPage />
          </VccPage>
        ),
      },
      {
        path: PATHS.VCC_PHONE_CHANNELS,
        label: "Phone Channels",
        element: (
          <VccPage>
            <PhoneChannelsPage />
          </VccPage>
        ),
      },
      {
        path: PATHS.VCC_EMAIL_CHANNELS,
        label: "Email Channels",
        element: (
          <VccPage>
            <EmailChannelsPage />
          </VccPage>
        ),
      },
      {
        path: PATHS.VCC_QUEUES,
        label: "Queues",
        element: (
          <VccPage>
            <QueuesPage />
          </VccPage>
        ),
      },
    ],
  },
  {
    path: PATHS.FRAUD_SUPPORT,
    label: "Fraud Support",
    icon: <LiveMonitorIcon />,
    element: <RequiredAuth />,
    errorElement: <ErrorBoundary />,
    permission: FRAUD_SUPPORT_READ,
    children: [
      {
        path: PATHS.SMS_AUDIT,
        label: "SMS Audit",
      },
    ],
  },
  ...(ENABLE_JOB_INVENTORY
    ? [
        {
          path: `${PATHS.JOBS}/:jobId?`,
          label: "My Jobs",
          icon: <InboxTrayIcon />,
          errorElement: <ErrorBoundary />,
          element: (
            <RequiredAuth>
              <JobInventoryPage />
            </RequiredAuth>
          ),
        },
      ]
    : []),
  {
    path: PATHS.TOKEN_GENERATOR,
    label: "Token Generator",
    icon: <IdentityManagementIcon />,
    element: (
      <RequiredAuth>
        <TokenGeneratorPage />
      </RequiredAuth>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: PATHS.TRANSCRIBE,
    label: "Transcribe",
    icon: <UploadIcon />,
    element: (
      <RequiredAuth>
        <TranscribePage />
      </RequiredAuth>
    ),
    errorElement: <ErrorBoundary />,
  },
  {
    path: "/implicit/callback",
    element: (
      <LoginCallback
        errorComponent={({ error }) => {
          throw error;
        }}
        loadingElement={<OverlaySpinner text={"Logging in..."} />}
      />
    ),
  },
];

export const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <ErrorBoundary />,
    children: routes,
  },
]);
